import { useMemo } from "react";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInstrumentRowContext } from '@dashboard/contexts/InstrumentRowContext';
import { useAnnouncementContext } from '@dashboard/contexts/ExchangeAnnContext';
import { useRowSelectionContext } from '@portfolio/context/RowSelectionContextProvider';
import {parseBroadcastTime, setGridItemHeight} from '@dashboard/grid-components/announcements/AnnouncementUtil';
import { callHandleShowAnnouncements } from '@dashboard/utils/RouterUtil';
import CloseIcon from '@mui/icons-material/Close';
import ReactDOM from 'react-dom';
import { isSameAnnouncement } from '@marketwatch/gridcomponents/util/GridOperations';
import '../css/AnnouncementStyles.css';

const MAX_HEIGHT_CONTAINER = 520;

const formatBroadcastTime = (s) => {
    const ts = parseBroadcastTime(s);
    if (!ts) return (s || '').toString();
    const d = new Date(ts);

    const dd = String(d.getUTCDate()).padStart(2, '0');
    const MMM = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
    const yyyy = d.getUTCFullYear();
    const HH = String(d.getUTCHours()).padStart(2, '0');
    const MM = String(d.getUTCMinutes()).padStart(2, '0');
    const SS = String(d.getUTCSeconds()).padStart(2, '0');

    // Single format everywhere: 12-Dec-2025 13:04:54
    return `${dd}-${MMM}-${yyyy} ${HH}:${MM}:${SS}`;
};

const isNse = (x) => ((x?.exchange || '').toString().toUpperCase() === 'NSE');

const timeKey = (x) => parseBroadcastTime(x?.BroadcastTime ?? x?.broadcastTime);

const msgKey = (x) => {
    if (!x) return '';
    return isNse(x)
        ? (x.Details ?? x.details ?? x.message ?? '').toString().trim()
        : (x.message ?? x.Details ?? x.details ?? '').toString().trim();
};

const subjectKey = (x) =>
    (x?.Subject ?? x?.subject ?? x?.announcementType ?? '').toString().trim();

const isSameAnnKeyLocal = (x, y) =>
    timeKey(x) === timeKey(y) &&
    msgKey(x) === msgKey(y) &&
    subjectKey(x) === subjectKey(y);

const isNseAnnouncement = (a) => (a?.exchange || '').toString().toUpperCase() === 'NSE';

const getAnnouncementTypeText = (a) => {
    if (!a) return '';
    if (isNseAnnouncement(a)) return a.Subject ?? a.subject ?? '';
    return a.announcementType ?? '';
};

const getAnnouncementMessageText = (a) => {
    if (!a) return '';
    if (isNseAnnouncement(a)) return a.Details ?? a.details ?? '';
    return a.message ?? '';
};

const getAnnouncementLink = (a) => {
    if (!a) return null;
    // NSE uses hrefLink; BSE uses fileLink
    return a.hrefLink ?? a.hreflink ?? a.fileLink ?? a.filelink ?? null;
};



const AnnouncementList = ({ calledBy }) => {
    const [selected, setSelected] = useState(null);
    const containerRef = useRef(null);
    const lastScrollHeightRef = useRef(null);
    const lastHeightUnitsRef = useRef(null);

    // ✅ Now we use the combined lists from context
    const {
        announcements,
        newAnnouncements,
        isUnmounting,
        setIsUnmounting,
        clearAnnouncements
    } = useAnnouncementContext();

    const { updateSelectedRowIndex, rowHeightGridLayout } = useRowSelectionContext();

    const {
        stock,
        resumeAutoRefreshNow,
        patchFrozenSnapshotInstrumentEntity,
        announcementStocksOnly,
        GUIInstrument,
        setGUIInstruments,
        setAggregatedSortedStocksCurrentClientTab,
        setLayouts,
        tabID,
        layouts,
        index,
        listRef,
        rowVirtualizer
    } = useInstrumentRowContext();

    const [isVisible, setIsVisible] = useState(false);

    useLayoutEffect(() => {
        requestAnimationFrame(() => setIsVisible(true));
    }, []);

    // Auto-resize the grid item height when announcement list changes
    useLayoutEffect(() => {
        if (isUnmounting || !containerRef.current) return;

        const listEl = containerRef.current.querySelector('.announcement-container');
        if (!listEl) return;

        // Measure only the scrollHeight of the announcement list
        const fullHeight = listEl.scrollHeight;
        const totalHeight = Math.min(fullHeight, MAX_HEIGHT_CONTAINER);
        const heightInGridUnits = (totalHeight / rowHeightGridLayout)+1;

        const prevHeight =
            typeof lastHeightUnitsRef.current === 'number' ? lastHeightUnitsRef.current : 0;
        const prevScroll =
            typeof lastScrollHeightRef.current === 'number' ? lastScrollHeightRef.current : 0;

        if (
            fullHeight === 0 ||
            (Math.abs(prevHeight - heightInGridUnits) < 0.1 && fullHeight === prevScroll)
        ) {
            return;
        }

        lastHeightUnitsRef.current = heightInGridUnits;
        lastScrollHeightRef.current = fullHeight;

        setGridItemHeight(heightInGridUnits, setLayouts, index);

        requestAnimationFrame(() => {
            const domNode = listRef?.current?.querySelector(
                `[data-row-key='${tabID}-${GUIInstrument.instrumentToken}']`
            );
            if (domNode) rowVirtualizer?.measureElement(domNode);
        });

    }, [
        announcements,
        newAnnouncements,
        isUnmounting,
        rowHeightGridLayout,
        setLayouts,
        index,
        listRef,
        tabID,
        GUIInstrument.instrumentToken,
        rowVirtualizer,
    ]);

    const handleRowSelect = () => {
        updateSelectedRowIndex(`${tabID}-${GUIInstrument.instrumentToken}`, index);
    };

    const handleClose = () => {
        // If user is in "announcement stocks only" mode, do NOT exit that mode on close.
        // Just close the announcements panel while keeping the portfolio frozen/filtered.
        if (announcementStocksOnly) {
            if (typeof patchFrozenSnapshotInstrumentEntity === 'function') {
                patchFrozenSnapshotInstrumentEntity(GUIInstrument?.instrumentToken, { forceShowAnnouncements: false });
            }
            callHandleShowAnnouncements({
                calledBy,
                GUIInstrument,
                setGUIInstruments,
                setAggregatedSortedStocksCurrentClientTab,
                setLayouts,
                tabID,
                layouts,
                index,
                announcements,
                newAnnouncements,
                setIsUnmounting,
                stock,
                listRef
            });
            return;
        }

        // Normal behaviour: allow close to take effect even if we're currently rendering a frozen snapshot,
        // then resume refreshing.
        if (typeof resumeAutoRefreshNow === 'function') {
            resumeAutoRefreshNow(false); // unfreeze without triggering refresh yet
        }
        callHandleShowAnnouncements({
            calledBy,
            GUIInstrument,
            setGUIInstruments,
            setAggregatedSortedStocksCurrentClientTab,
            setLayouts,
            tabID,
            layouts,
            index,
            announcements,
            newAnnouncements,
            setIsUnmounting,
            stock,
            listRef
        });
        if (typeof resumeAutoRefreshNow === 'function') {
            resumeAutoRefreshNow(true);
        }
    };

    // ESC closes detail modal first, then the list
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape' || e.code === 'Escape') {
                if (selected) {
                    setSelected(null);
                } else {
                    handleClose();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selected, handleClose]);

    const sortedAnnouncements = useMemo(() => {
        return [...announcements].sort(
            (a, b) => parseBroadcastTime(b.BroadcastTime ?? b.broadcastTime) -
                parseBroadcastTime(a.BroadcastTime ?? a.broadcastTime)
        );
    }, [announcements]);

    return (
        <>
            <div  className={"annlist-wrapper"}  ref={containerRef}>
                <div
                    className={`announcement-container ${calledBy}-announcement-container`}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="announcement-clearall-container">
                        <button
                            className={`clear-all-button ${
                                newAnnouncements.length === 0 ? 'disabled' : ''
                            }`}
                            disabled={newAnnouncements.length === 0}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (newAnnouncements.length > 0) {
                                    // clear ALL BSE+NSE new announcements
                                    clearAnnouncements([]);
                                    handleClose();
                                }
                            }}
                        >
                            Clear All
                        </button>

                        <button onClick={handleClose} className="close-announcements-btn">
                            Close
                        </button>
                    </div>

                    <div
                        className="announcement-list-container"
                        style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.4s ease-in-out' }}
                    >
                        <ul className="announcement-list-exchange">
                            {sortedAnnouncements.length === 0 ? (
                                <li className="no-announcements announcement-item-ex">
                                    No Announcements
                                </li>
                            ) : (
                                sortedAnnouncements.map((a, idx) => {
                                    const isNew = newAnnouncements.includes(a);
                                    return (
                                        <li
                                            key={idx}
                                            className={`announcement-item-ex ${
                                                isNew ? 'announcement-new' : ''
                                            }`}
                                            onClick={() => {
                                                setSelected({ ...a, isNew });
                                                handleRowSelect();
                                            }}
                                        >
                                            <div className="announcement-line">
                                                <span className="announcement-type">
                                                    {getAnnouncementTypeText(a)}
                                                </span>

                                                {/* ✅ Exchange column */}
                                                <span className="announcement-exchange">
                                                    {a.exchange || ''}
                                                </span>

                                                <span className="announcement-message">
                                                    {getAnnouncementMessageText(a)}
                                                </span>
                                                <span className="announcement-time">
                                                    {formatBroadcastTime(a.BroadcastTime)}
                                                </span>
                                                <span className="announcement-clear-span">
                                                    {isNew && (
                                                        <button
                                                            className="clear-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                // Clear this one (BSE or NSE).
                                                                clearAnnouncements(prev => {
                                                                    const next = prev.filter(ann => !isSameAnnKeyLocal(ann, a));

                                                                    // 👇 if this was the last new announcement, close the panel
                                                                    if (next.length === 0) {
                                                                        // defer close slightly to let state settle
                                                                        setTimeout(() => handleClose(), 0);
                                                                    }

                                                                    return next;
                                                                });
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </button>
                                                    )}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })
                            )}
                        </ul>
                    </div>
                </div>

                {selected &&
                    ReactDOM.createPortal(
                        <div className="announcement-modal">
                            <div className="announcement-annpopup">
                                <div className="annpopup-header">
                                    <h4>Announcement Details</h4>
                                    <button
                                        onClick={() => setSelected(null)}
                                        className="annpopup-close"
                                    >
                                        &times;
                                    </button>
                                </div>

                                <div className="annpopup-content-table">
                                    <div className="annpopup-table">
                                        {Object.entries(selected).map(([key, value]) => (
                                            <div key={key} className="annpopup-row">
                                                <div className="annpopup-cell annpopup-label">
                                                    {key.charAt(0).toUpperCase() +
                                                        key.slice(1)}
                                                    :
                                                </div>
                                                <div className="annpopup-cell annpopup-value">
                                                    {(() => {
                                                        const k = key.toLowerCase();
                                                        const isLinkField = (k === 'filelink' || k === 'hreflink');
                                                        const v = value;
                                                        if (isLinkField && v) {
                                                            return (
                                                                <a
                                                                    href={v}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {v}
                                                                </a>
                                                            );
                                                        }
                                                        return (
                                                            v
                                                                ?.toString()
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                            v?.toString().slice(1)
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="contract-buttons">
                                    <button
                                        className="annpopup-clear-button"
                                        style={{
                                            visibility: selected.isNew
                                                ? 'visible'
                                                : 'hidden'
                                        }}
                                        onClick={() => {
                                            // Clear this single announcement using same semantics as before
                                            clearAnnouncements(prev => prev.filter(ann => !isSameAnnKeyLocal(ann, selected)));
                                            setSelected(null);
                                        }}
                                    >
                                        Clear
                                    </button>
                                    <button
                                        onClick={() => setSelected(null)}
                                        className="cancel-button"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.getElementById('modal-root')
                    )}
            </div>
        </>
    );
};

export default AnnouncementList;
